import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAssetPersist from "src/api/asset-persist/stores/useAssetPersist";
import { UploadAssetInfo } from "src/api/asset-persist/types";
import GetGatheringAndPhotosDocument from "src/api/graphql/__generated__/documents/GetGatheringAndPhotosDocument";
import GetGatheringAndPhotosWithBlurbsDocument from "src/api/graphql/__generated__/documents/GetGatheringAndPhotosWithBlurbsDocument";
import FaceFilterMode from "src/api/graphql/__generated__/enums/FaceFilterMode";
import GatheringPhotosFilter from "src/api/graphql/__generated__/enums/GatheringPhotosFilter";
import PaginationDirection from "src/api/graphql/__generated__/enums/PaginationDirection";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import {
	GatheringPhotoSignedUrlFragmentFragment,
	GetGatheringAndPhotosQuery,
	GetGatheringAndPhotosWithBlurbsQuery,
} from "src/api/graphql/__generated__/graphql";
import { getClientId } from "src/helpers/globalHelpers";
import useStateRef from "src/hooks/useStateRef";
import { Timeout } from "src/types/time";
import useMountEffect from "../useMountEffect";
import useStabilizedCallback from "../useStabilizedCallback";
import useSyncRef from "../useSyncRef";

type GQLPhotoItem = NonNullable<
	NonNullable<
		(GetGatheringAndPhotosQuery | GetGatheringAndPhotosWithBlurbsQuery)["getGathering"]
	>["photos"]
>["payload"][number];

export type GatheringOrLocalPhoto = (
	| (GQLPhotoItem & {
			isLocal?: false;
	  })
	| (Partial<GQLPhotoItem> & {
			photoId: string;
			ownerId: string;
			readUrl: {
				url: string | null;
				stableUrl: string | null;
				videoUrl: string | null;
				stableVideoUrl: string | null;
			} & GatheringPhotoSignedUrlFragmentFragment;
			isLocal: true;
			variant: PhotoVariant;
			timestamp?: number;
	  })
) & {
	type: "Photo";
};

const useParsedLocalPhotos = ({ gatheringId }: { gatheringId: string | null | undefined }) => {
	const [initialLocalPhotos] = useState<UploadAssetInfo[]>(() =>
		Object.values(useAssetPersist.getState().uploadAssetsStore),
	);
	const curLocalPhotos = useRef<UploadAssetInfo[]>(initialLocalPhotos);
	const timeoutRef = useRef<Timeout | null>(null);
	const processCurLocalPhotos = useCallback(() => {
		const retPhotos: GatheringOrLocalPhoto[] = [];
		const clientId = getClientId();

		// Most recent photos first
		for (const localPhoto of curLocalPhotos.current) {
			if (
				!(localPhoto.gatheringId && gatheringId && localPhoto.gatheringId === gatheringId)
			) {
				continue;
			}
			if (
				localPhoto.uploadState !== "Uploaded" &&
				localPhoto.uploadState !== "Confirming" &&
				localPhoto.uploadState !== "Processed"
			) {
				continue;
			}
			if (!localPhoto.uploadedPhotoInfo) continue;

			const parsedPhoto = {
				type: "Photo",
				ownerId: clientId,
				photoId: localPhoto.uploadedPhotoInfo.photoId,
				readUrl: {
					url: localPhoto.uploadedPhotoInfo.url ?? null,
					stableUrl:
						// Videos shouldn't use optimisticOptimizedUrls since they don't process in time and they mess up the processing display
						(localPhoto.uriInfo.variant === PhotoVariant.Video
							? null
							: localPhoto.uploadedPhotoInfo?.optimisticOptimizedUrl) ??
						localPhoto.uriInfo.image?.uri ??
						null,
					videoUrl: localPhoto.uploadedPhotoInfo.videoUrl ?? null,
					stableVideoUrl: localPhoto.uriInfo.video?.uri ?? null,
				},
				state: PhotoState.Preparing,
				variant: localPhoto.uriInfo.variant,
				isLocal: true,
			} satisfies GatheringOrLocalPhoto;
			retPhotos.push(parsedPhoto);
		}
		// Timestamps tend to be the same for batch uploads, so we reverse the array to make it more accurate to expectations
		const sortedRetPhotos = (retPhotos as { timestamp: number }[]).reverse().sort((a, b) => {
			return b.timestamp - a.timestamp;
		}) as GatheringOrLocalPhoto[];

		return sortedRetPhotos;
	}, [gatheringId]);
	const [parsedLocalPhotos, setParsedLocalPhotos] = useState<GatheringOrLocalPhoto[]>(() =>
		processCurLocalPhotos(),
	);

	const clearLocalPhotos = useCallback(() => {
		setParsedLocalPhotos([]);
	}, []);

	useEffect(() => {
		const unsubscribe = useAssetPersist.subscribe(
			(state) => state.uploadAssetsStore,
			(store) => {
				curLocalPhotos.current = Object.values(store);
				if (timeoutRef.current === null) {
					// Debouncing for performance reasons
					timeoutRef.current = setTimeout(() => {
						timeoutRef.current = null;
						setParsedLocalPhotos(processCurLocalPhotos());
					}, 1000);
				}
			},
		);
		return unsubscribe;
	}, [processCurLocalPhotos]);

	return {
		parsedLocalPhotos,
		clearLocalPhotos,
	};
};

const useGatheringPhotosWithLocal = (
	gatheringId: string,
	{
		initialRefreshAmount,
		numColumns = 1,
		baseLimit = 16,
		filterUserIds,
		filterUploaderIds,
		filterPhotoVariant,
		filterLabels,
		filterRestrictedLabels,
		filterMode = FaceFilterMode.Or,
		fromPhotoId: initialFromPhotoId,
		enabled = true,
		shouldInitialRefresh = (data) => !data?.getGathering?.photos?.payload?.length,
		onFetchMore,
		key,
		filter,
	}: {
		initialRefreshAmount?: number;
		numColumns?: number;
		baseLimit?: number;
		filterUserIds?: string[];
		filterUploaderIds?: string[];
		filterPhotoVariant?: PhotoVariant[];
		filterLabels?: string[];
		filterRestrictedLabels?: string[];
		filterMode?: FaceFilterMode;
		fromPhotoId?: string;
		enabled?: boolean;
		/**
		 * Determines whether or not to do an initial refresh. If this is not provided, it will do an initial refresh if there is no data.
		 */
		shouldInitialRefresh?:
			| ((data: GetGatheringAndPhotosQuery | undefined) => Promise<boolean> | boolean)
			| boolean;
		onFetchMore?: (args: { prevNumPhotos: number; prevNumFetchMore: number }) => void;
		key?: string;
		filter?: GatheringPhotosFilter;
	} = {},
) => {
	const enabledRef = useSyncRef(enabled);
	const fromPhotoId = useRef(initialFromPhotoId).current;
	const filterUserIdsRef = useSyncRef(filterUserIds);
	const filterUploaderIdsRef = useSyncRef(filterUploaderIds);
	const filterPhotoVariantRef = useSyncRef(filterPhotoVariant);
	const filterLabelsRef = useSyncRef(filterLabels);
	const filterRestrictedLabelsRef = useSyncRef(filterRestrictedLabels);

	const filterModeRef = useSyncRef(filterMode);
	const filterRef = useSyncRef(filter);
	const photosLenRef = useRef(0);
	const getPhotosInput = useCallback(
		(overrideBaseLimit?: number) => {
			return {
				limit: overrideBaseLimit ?? baseLimit * numColumns,
				filterUserIds: filterUserIdsRef.current,
				filterUploaderIds: filterUploaderIdsRef.current,
				filterPhotoVariant: filterPhotoVariantRef.current,
				filterLabels: filterLabelsRef.current,
				filterRestrictedLabels: filterRestrictedLabelsRef.current,
				filterMode: filterModeRef.current,
				filter: filterRef.current,
				key,
			};
		},
		[
			baseLimit,
			key,
			numColumns,
			filterModeRef,
			filterRef,
			filterUserIdsRef,
			filterUploaderIdsRef,
			filterPhotoVariantRef,
			filterLabelsRef,
			filterRestrictedLabelsRef,
		],
	);

	const {
		data,
		previousData,
		loading: queryLoading,
		fetchMore,
		refetch,
	} = useQuery(
		filter === GatheringPhotosFilter.AllCaller || filter === GatheringPhotosFilter.Hidden
			? GetGatheringAndPhotosWithBlurbsDocument
			: GetGatheringAndPhotosDocument,
		{
			variables: {
				input: {
					gatheringId: gatheringId!,
				},
				photosInput: {
					...getPhotosInput(initialRefreshAmount),
					// Important to have the reactive version here for state updates
					// Need to parse options to the base options matches what we're fetching more with
					filterUserIds,
					filterPhotoVariant,
					filterUploaderIds,
					filterMode,
					filterLabels,
					filterRestrictedLabels,
					direction: fromPhotoId
						? PaginationDirection.BiDirectional
						: PaginationDirection.Forward,
					fromPhotoId: fromPhotoId,
					key,
				},
			},
			fetchPolicy: "cache-first",
			returnPartialData: false,
		},
	);

	const hasActiveFilter = useMemo(() => {
		return (
			(filterUserIds?.length ?? 0) > 0 ||
			(filterUploaderIds?.length ?? 0) > 0 ||
			(filterPhotoVariant?.length ?? 0) > 0 ||
			(filterLabels?.length ?? 0) > 0 ||
			(filterRestrictedLabels?.length ?? 0) > 0
		);
	}, [
		filterUserIds?.length,
		filterUploaderIds?.length,
		filterPhotoVariant?.length,
		filterLabels?.length,
		filterRestrictedLabels?.length,
	]);

	// Note: You may want to use `previousData` here, but that tends to run into issues with the server-side filters
	// Means we'll show stale data while loading
	const gathering = data?.getGathering;

	const cursor = useRef<string | null>(data?.getGathering?.photos?.cursor ?? null);
	useEffect(() => {
		cursor.current = data?.getGathering?.photos?.cursor ?? null;
	}, [data?.getGathering?.photos?.cursor]);
	const reverseCursor = useRef<string | null>(data?.getGathering?.photos?.reverseCursor ?? null);
	useEffect(() => {
		reverseCursor.current = data?.getGathering?.photos?.reverseCursor ?? null;
	}, [data?.getGathering?.photos?.reverseCursor]);

	const [loading, setLoading, loadingRef] = useStateRef(false);
	const [refreshing, setRefreshing, refreshingRef] = useStateRef(false);

	const { parsedLocalPhotos } = useParsedLocalPhotos({
		gatheringId,
	});

	const numFetchMore = useRef(0);
	const handleFetchMore = useStabilizedCallback(async () => {
		if (!cursor.current) return;
		if (loadingRef.current) return;
		if (refreshingRef.current) return;
		if (cursor.current === "{}") return;
		if (!enabledRef.current) return;
		// No reason to fetch more if there are no photos -- should be handled by refresh
		if (photosLenRef.current === 0) return;
		onFetchMore?.({
			prevNumPhotos: photosLenRef.current,
			prevNumFetchMore: numFetchMore.current,
		});
		numFetchMore.current += 1;

		setLoading(true);
		try {
			await fetchMore({
				variables: {
					input: {
						gatheringId: gatheringId!,
					},
					photosInput: {
						cursor: cursor.current,
						...getPhotosInput(),
					},
				},
			});
		} finally {
			setLoading(false);
		}
	}, [
		loadingRef,
		refreshingRef,
		enabledRef,
		onFetchMore,
		setLoading,
		fetchMore,
		gatheringId,
		getPhotosInput,
	]);

	const handleFetchMoreReverse = useStabilizedCallback(async () => {
		if (!reverseCursor.current) return;
		if (loadingRef.current) return;
		if (refreshingRef.current) return;
		if (reverseCursor.current === "{}") return;
		if (!enabledRef.current) return;

		setLoading(true);
		try {
			await fetchMore({
				variables: {
					input: {
						gatheringId: gatheringId!,
					},
					photosInput: {
						...getPhotosInput(),
						cursor: reverseCursor.current,
						direction: PaginationDirection.Reverse,
					},
				},
			});
		} finally {
			setLoading(false);
		}
	}, [loadingRef, refreshingRef, enabledRef, setLoading, fetchMore, gatheringId, getPhotosInput]);

	const handleRefresh = useStabilizedCallback(
		async ({ isInitial = false, force }: { isInitial?: boolean; force?: boolean } = {}) => {
			if (!force) {
				if (refreshingRef.current) return;
				if (loadingRef.current) return;
				if (!enabledRef.current) return;
			}

			try {
				setRefreshing(true);
				const basePhotosInput = getPhotosInput();
				if (isInitial && initialRefreshAmount) {
					basePhotosInput.limit = initialRefreshAmount;
				}
				const refetchRes = await refetch({
					input: {
						gatheringId: gatheringId!,
					},
					photosInput: {
						...basePhotosInput,
						direction: fromPhotoId
							? PaginationDirection.BiDirectional
							: PaginationDirection.Forward,
						fromPhotoId: fromPhotoId,
					},
				});
				if (!refetchRes) {
					throw new Error("Gathering photos refetch failed; missing res");
				}
			} finally {
				setRefreshing(false);
			}
		},
		[
			enabledRef,
			fromPhotoId,
			gatheringId,
			getPhotosInput,
			initialRefreshAmount,
			loadingRef,
			refetch,
			refreshingRef,
			setRefreshing,
		],
	);

	const didInit = useRef(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: We have extra fields we want to force a refresh on
	useEffect(() => {
		if (didInit.current) {
			handleRefresh({
				force: true,
			}).then(() => {
				handleFetchMore();
			});
		}
	}, [filterMode, filterUserIds, handleFetchMore, handleRefresh]);

	useMountEffect(() => {
		if (didInit.current) return;
		didInit.current = true;
		(async () => {
			if (
				typeof shouldInitialRefresh === "function"
					? await shouldInitialRefresh(data)
					: shouldInitialRefresh
			) {
				handleRefresh({
					isInitial: true,
					force: true,
				}).then(() => {
					handleFetchMore();
					if (fromPhotoId) {
						handleFetchMoreReverse();
					}
				});
			} else {
				handleFetchMore();
				if (fromPhotoId) {
					handleFetchMoreReverse();
				}
			}
		})();
	}, []);

	const photos = useMemo(() => {
		const includeLocalPhotos =
			// If the user is filtering, we can't trust the local photos to be correct
			filter !== GatheringPhotosFilter.ActiveCaptureSort && !hasActiveFilter;
		// Order of concatenation here matters; we want local photos to come first
		const combinedPhotos = (includeLocalPhotos ? parsedLocalPhotos : []).concat(
			gathering?.photos?.payload?.map((photo) => ({
				...photo,
				type: "Photo",
			})) ?? [],
		) as GatheringOrLocalPhoto[];
		const retPhotos: Map<string, GatheringOrLocalPhoto> = new Map();
		const clientId = getClientId();
		for (const photo of combinedPhotos) {
			// Filter checks
			if (
				filter === GatheringPhotosFilter.Active &&
				photo.state !== PhotoState.Active &&
				photo.ownerId !== clientId
			) {
				continue;
			}
			if (filter === GatheringPhotosFilter.Hidden && photo.state !== PhotoState.Hidden) {
				continue;
			}
			if (filter === GatheringPhotosFilter.AllCaller && photo.ownerId !== clientId) {
				continue;
			}
			if (
				filter === GatheringPhotosFilter.Active &&
				photo.state !== PhotoState.Active &&
				!(photo.state === PhotoState.Preparing && photo.ownerId === clientId)
			) {
				continue;
			}

			const photoId = photo.photoId;
			// Doing this to ensure currect order
			if (retPhotos.has(photoId)) {
				retPhotos.delete(photoId);
			}
			retPhotos.set(photoId, photo);
		}
		const retPhotosArray = Array.from(retPhotos.values());
		photosLenRef.current = retPhotosArray.length;
		return retPhotosArray;
	}, [parsedLocalPhotos, gathering?.photos?.payload, filter, hasActiveFilter]);

	const retLoading = useMemo(() => loading || queryLoading, [loading, queryLoading]);

	return {
		photos,
		fetchMore: handleFetchMore,
		fetchMoreReverse: handleFetchMoreReverse,
		refresh: handleRefresh,
		refreshing: refreshing,
		loading: retLoading,
		curGathering: gathering,
	};
};

export default useGatheringPhotosWithLocal;
